import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { History, Crown, LogOut, ChevronRight, Radio, Menu, Zap, TrendingUp, Quote, Sparkles, User, Shield, Settings } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { motion } from 'framer-motion';
import { HOST_IMAGE, EXPERT_IMAGES, EXPERT_INFO } from '@/data/experts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Technology: '#06B6D4', Finance: '#F59E0B', Business: '#10B981', Psychology: '#A78BFA',
  Health: '#34D399', Culture: '#F472B6', Politics: '#64748B', History: '#D97706',
  Science: '#38BDF8', Philosophy: '#C084FC', Law: '#94A3B8',
};

const FEATURED_EXPERTS = ['philosopher', 'economist', 'comedian', 'military_general', 'scientist', 'entrepreneur'];

const HomePage = () => {
  const [topic, setTopic] = useState('');
  const [stats, setStats] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [trending, setTrending] = useState([]);
  const [sharedQuotes, setSharedQuotes] = useState([]);
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const isGuest = !user;

  useEffect(() => {
    const fetchPublicData = async () => {
      try {
        const [trendingRes, quotesRes] = await Promise.all([
          axios.get(`${API}/trending`).catch(() => null),
          axios.get(`${API}/shared-quotes`).catch(() => null),
        ]);
        if (trendingRes) setTrending(trendingRes.data.topics || []);
        if (quotesRes) setSharedQuotes(quotesRes.data.quotes || []);
      } catch (e) { console.error('Failed to fetch public data:', e); }
    };
    fetchPublicData();
  }, []);

  useEffect(() => {
    if (!token) return;
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/user/stats`, { headers: { Authorization: `Bearer ${token}` } });
        setStats(res.data);
      } catch (e) { console.error('Failed to fetch user stats:', e); }
    };
    fetchStats();
  }, [token]);

  // Require auth for protected actions - redirect with intent
  const requireAuth = (intent) => {
    if (isGuest) {
      navigate('/auth', { state: { from: { pathname: '/' }, intent } });
      return true;
    }
    return false;
  };

  const handleGoLive = async (topicText) => {
    const t = topicText || topic.trim();
    if (!t) { toast.error('Enter a topic first'); return; }
    if (!token || isGuest) {
      navigate('/auth', { state: { from: { pathname: '/' }, intent: { action: 'launch', topic: t } } });
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/podcasts/generate`, { topic: t }, { headers: { Authorization: `Bearer ${token}` } });
      navigate(`/loading/${response.data.podcast.podcast_id}`);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to start podcast';
      toast.error(detail);
      setGenerating(false);
    }
  };

  const handleTrendingClick = (t) => {
    handleGoLive(t.title);
  };

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#08080A' }}>
      {/* Subtle grid */}
      <div className="fixed inset-0 opacity-[0.025] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(109,40,217,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(109,40,217,0.4) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />

      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b px-4 sm:px-8 py-3" style={{ borderColor: 'rgba(255,255,255,0.05)', backgroundColor: 'rgba(8,8,10,0.9)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Radio className="w-5 h-5 text-purple-500" />
            <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}>Opposite Direction</span>
            <div className="flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider" style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />LIVE
            </div>
          </div>

          {/* Auth-dependent nav */}
          {isGuest ? (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/auth')} className="text-gray-400 hover:text-white hover:bg-white/5 text-xs" data-testid="nav-signin-btn">
                <User className="w-3.5 h-3.5 mr-1.5" />Sign In
              </Button>
              <Button size="sm" onClick={() => navigate('/auth', { state: { register: true } })} className="bg-purple-600 hover:bg-purple-500 text-white text-xs h-8 px-4 rounded-lg" data-testid="nav-get-started-btn">
                Get Started
              </Button>
            </div>
          ) : (
            <>
              <div className="hidden sm:flex items-center gap-3">
                {user?.role === 'admin' || user?.role === 'super_admin' ? (
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-gray-500 hover:text-white hover:bg-white/5 text-xs" data-testid="admin-btn"><Shield className="w-3.5 h-3.5 mr-1.5" />Admin</Button>
                ) : null}
                <Button variant="ghost" size="sm" onClick={() => navigate('/history')} className="text-gray-500 hover:text-white hover:bg-white/5 text-xs" data-testid="history-btn"><History className="w-3.5 h-3.5 mr-1.5" />History</Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/subscription')} className="text-gray-500 hover:text-white hover:bg-white/5 text-xs" data-testid="subscription-btn"><Crown className="w-3.5 h-3.5 mr-1.5" />{stats?.is_premium ? 'Premium' : 'Upgrade'}</Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="text-gray-500 hover:text-white hover:bg-white/5 text-xs" data-testid="settings-btn"><Settings className="w-3.5 h-3.5 mr-1.5" />Settings</Button>
                <div className="pl-3 border-l" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                  <Button variant="ghost" size="icon" onClick={async () => { await logout(); navigate('/'); }} className="text-gray-500 hover:text-white hover:bg-white/5" data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="sm:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="text-gray-400"><Menu className="w-5 h-5" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44 bg-[#141417] border-white/10">
                    {(user?.role === 'admin' || user?.role === 'super_admin') && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="text-gray-300 focus:text-white focus:bg-white/5"><Shield className="w-4 h-4 mr-2" />Admin</DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/history')} className="text-gray-300 focus:text-white focus:bg-white/5"><History className="w-4 h-4 mr-2" />History</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/subscription')} className="text-gray-300 focus:text-white focus:bg-white/5"><Crown className="w-4 h-4 mr-2" />Upgrade</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings')} className="text-gray-300 focus:text-white focus:bg-white/5"><Settings className="w-4 h-4 mr-2" />Settings</DropdownMenuItem>
                    <DropdownMenuItem onClick={async () => { await logout(); navigate('/'); }} className="text-gray-300 focus:text-white focus:bg-white/5"><LogOut className="w-4 h-4 mr-2" />Logout</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          )}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* ===== HERO SECTION ===== */}
        <section className="py-12 sm:py-20 relative">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full opacity-[0.07] pointer-events-none" style={{ background: 'radial-gradient(ellipse, #6D28D9, transparent 70%)' }} />

          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Left: Text + CTA */}
            <div className="flex-1 text-center lg:text-left">
              <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="text-xs sm:text-sm tracking-[0.25em] uppercase mb-4" style={{ color: '#6D28D9' }}>
                AI-Powered Debate Podcast
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.1] mb-5"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }} data-testid="hero-title">
                Opposite Direction<br />Podcast
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-sm sm:text-base mb-8 max-w-md mx-auto lg:mx-0" style={{ color: '#64748B' }}>
                Pick any topic. 28 AI agents with opposing worldviews will argue, disagree, and reveal uncomfortable truths — in real voices.
              </motion.p>

              {/* CTA Input */}
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="max-w-xl mx-auto lg:mx-0">
                <div className="rounded-2xl p-1 border" style={{ backgroundColor: 'rgba(20,20,23,0.8)', borderColor: 'rgba(109,40,217,0.25)' }}>
                  <div className="flex gap-2">
                    <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="What should 28 AI experts debate?" className="flex-1 h-13 sm:h-14 text-sm sm:text-base bg-transparent border-0 text-white placeholder:text-gray-600 focus-visible:ring-0" onKeyDown={(e) => e.key === 'Enter' && !generating && handleGoLive()} disabled={generating} data-testid="topic-input" />
                    <Button onClick={() => handleGoLive()} disabled={generating || !topic.trim()} className="h-13 sm:h-14 px-5 sm:px-8 bg-red-600 hover:bg-red-500 text-white font-semibold rounded-xl disabled:opacity-40 text-sm" data-testid="go-live-btn">
                      {generating ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Zap className="w-4 h-4 mr-1.5" />Go Live</>}
                    </Button>
                  </div>
                </div>
                {!isGuest && stats && (
                  <div className="mt-3 flex items-center gap-3 text-[11px] justify-center lg:justify-start" style={{ color: '#64748B' }}>
                    <span><span className="text-white font-medium">{stats.remaining_today}</span> broadcasts left today</span>
                    <span>|</span>
                    <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: stats.is_premium ? '#10B981' : '#6D28D9' }} />{stats.is_premium ? 'Premium' : 'Free Tier'}</span>
                  </div>
                )}
                {isGuest && (
                  <div className="mt-3 flex items-center gap-3 text-[11px] justify-center lg:justify-start" style={{ color: '#64748B' }}>
                    <span>Free to try — no credit card required</span>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Right: Animated Roundtable Scene */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="flex-shrink-0 relative" data-testid="hero-roundtable">
              <div className="relative w-[280px] h-[280px] sm:w-[340px] sm:h-[340px]">
                <div className="absolute inset-8 rounded-full" style={{ background: 'radial-gradient(circle, rgba(109,40,217,0.15) 0%, transparent 70%)', boxShadow: '0 0 80px rgba(109,40,217,0.15)' }} />
                <div className="absolute inset-12 rounded-full border" style={{ borderColor: 'rgba(109,40,217,0.2)' }} />
                <div className="absolute inset-6 rounded-full border animate-pulse" style={{ borderColor: 'rgba(109,40,217,0.1)' }} />

                {/* Brand ring text */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                  <div className="absolute" style={{ top: '6%' }}>
                    <span className="text-[10px] sm:text-xs tracking-[0.3em] uppercase font-medium" style={{ color: 'rgba(167,139,250,0.5)' }}>Opposite Direction</span>
                  </div>
                  <div className="absolute" style={{ bottom: '6%' }}>
                    <span className="text-[9px] sm:text-[10px] tracking-[0.25em] uppercase" style={{ color: 'rgba(100,116,139,0.4)' }}>Podcast</span>
                  </div>
                </div>

                {/* Host center */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 overflow-hidden z-10 shadow-[0_0_20px_rgba(109,40,217,0.4)]" style={{ borderColor: '#6D28D9' }}>
                  <img src={HOST_IMAGE} alt="Host" className="w-full h-full object-cover" />
                </div>

                {/* Expert avatars around the table */}
                {FEATURED_EXPERTS.map((eid, i) => {
                  const angle = (i / FEATURED_EXPERTS.length) * Math.PI * 2 - Math.PI / 2;
                  const radius = 120;
                  const x = Math.cos(angle) * radius;
                  const y = Math.sin(angle) * radius;
                  return (
                    <motion.div
                      key={eid}
                      className="absolute w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 overflow-hidden"
                      style={{ top: `calc(50% + ${y}px - 24px)`, left: `calc(50% + ${x}px - 24px)`, borderColor: i === 0 ? '#A78BFA' : '#1C1C21', zIndex: 5 }}
                      animate={i === 0 ? { boxShadow: ['0 0 0px rgba(167,139,250,0)', '0 0 20px rgba(167,139,250,0.5)', '0 0 0px rgba(167,139,250,0)'] } : {}}
                      transition={{ duration: 2, repeat: Infinity, delay: i * 0.4 }}
                    >
                      <img src={EXPERT_IMAGES[eid]} alt={eid} className="w-full h-full object-cover" />
                    </motion.div>
                  );
                })}

                {/* Floating waveform dots */}
                {Array.from({ length: 8 }).map((_, i) => {
                  const angle = (i / 8) * Math.PI * 2;
                  const r = 95;
                  return (
                    <motion.div key={`dot-${i}`} className="absolute w-1 h-1 rounded-full bg-purple-500"
                      style={{ top: `calc(50% + ${Math.sin(angle) * r}px)`, left: `calc(50% + ${Math.cos(angle) * r}px)` }}
                      animate={{ opacity: [0.1, 0.6, 0.1], scale: [1, 1.5, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: i * 0.25 }}
                    />
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ===== FEATURED AI HOSTS ===== */}
        <section className="py-10 border-t" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}>Meet the AI Hosts</h2>
              <p className="text-xs mt-1" style={{ color: '#64748B' }}>28 AI agents, each with their own worldview</p>
            </div>
            <Button variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300 text-xs" onClick={() => isGuest ? navigate('/auth', { state: { from: { pathname: '/experts' } } }) : navigate('/experts')}>View All <ChevronRight className="w-3 h-3 ml-1" /></Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {FEATURED_EXPERTS.map((eid, i) => {
              const info = EXPERT_INFO[eid];
              const catColor = CATEGORY_COLORS[info?.category] || '#6D28D9';
              return (
                <motion.div key={eid} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
                  className="group rounded-2xl overflow-hidden border cursor-pointer transition-all hover:border-purple-500/30 hover:shadow-[0_0_30px_rgba(109,40,217,0.1)]"
                  style={{ backgroundColor: 'rgba(15,15,18,0.8)', borderColor: 'rgba(255,255,255,0.06)' }}
                  data-testid={`featured-expert-${eid}`}
                >
                  <div className="aspect-square overflow-hidden relative">
                    <img src={EXPERT_IMAGES[eid]} alt={info?.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-2 left-2 right-2">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[9px] font-semibold" style={{ backgroundColor: `${catColor}20`, color: catColor }}>{info?.category}</span>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium truncate" style={{ color: '#F8FAFC' }}>{info?.name}</p>
                    <p className="text-[10px] truncate" style={{ color: '#64748B' }}>{info?.title}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ===== DAILY TRENDING ROUNDTABLES ===== */}
        {trending.length > 0 && (
          <section className="py-10 border-t" style={{ borderColor: 'rgba(255,255,255,0.04)' }} data-testid="trending-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2.5">
                <TrendingUp className="w-5 h-5 text-red-400" />
                <div>
                  <h2 className="text-xl sm:text-2xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}>Trending Debates</h2>
                  <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>Today's hottest debates</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {trending.slice(0, 6).map((t, i) => {
                const catColor = CATEGORY_COLORS[t.category] || '#6D28D9';
                const experts = t.suggested_experts || [];
                return (
                  <motion.div key={t.topic_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 * i }}
                    className="group rounded-2xl border overflow-hidden cursor-pointer transition-all hover:border-purple-500/30 hover:shadow-[0_0_30px_rgba(109,40,217,0.1)]"
                    style={{ backgroundColor: 'rgba(15,15,18,0.8)', borderColor: 'rgba(255,255,255,0.06)' }}
                    onClick={() => handleTrendingClick(t)} data-testid={`trending-card-${i}`}
                  >
                    <div className="relative h-24 overflow-hidden" style={{ background: `linear-gradient(135deg, ${catColor}20, rgba(109,40,217,0.1))` }}>
                      <div className="absolute bottom-3 left-4 flex -space-x-2">
                        {experts.length > 0 ? experts.slice(0, 3).map(eid => (
                          <div key={eid} className="w-10 h-10 rounded-full border-2 overflow-hidden" style={{ borderColor: '#0F0F12' }}>
                            <img src={EXPERT_IMAGES[eid] || HOST_IMAGE} alt={eid} className="w-full h-full object-cover" />
                          </div>
                        )) : (
                          <div className="flex -space-x-2">
                            <div className="w-10 h-10 rounded-full border-2 overflow-hidden" style={{ borderColor: '#0F0F12' }}>
                              <img src={HOST_IMAGE} alt="host" className="w-full h-full object-cover" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>
                        <TrendingUp className="w-2.5 h-2.5" />{t.trending_score}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-semibold" style={{ backgroundColor: `${catColor}15`, color: catColor }}>{t.category}</span>
                      </div>
                      <h3 className="text-sm font-medium mb-1 line-clamp-2 group-hover:text-purple-300 transition-colors" style={{ color: '#F8FAFC' }}>{t.title}</h3>
                      <p className="text-[11px] line-clamp-2" style={{ color: '#64748B' }}>{t.description}</p>
                      <div className="mt-3 flex items-center gap-1.5 text-[10px] font-medium" style={{ color: '#A78BFA' }}>
                        <Zap className="w-3 h-3" />Listen Live
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            {trending.length > 6 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {trending.slice(6).map((t, i) => {
                  const catColor = CATEGORY_COLORS[t.category] || '#6D28D9';
                  return (
                    <div key={t.topic_id} className="group rounded-xl border p-3 cursor-pointer transition-all hover:border-purple-500/20" style={{ backgroundColor: 'rgba(15,15,18,0.6)', borderColor: 'rgba(255,255,255,0.04)' }} onClick={() => handleTrendingClick(t)} data-testid={`trending-small-${i}`}>
                      <span className="inline-block px-1.5 py-0.5 rounded text-[8px] font-semibold mb-2" style={{ backgroundColor: `${catColor}10`, color: catColor }}>{t.category}</span>
                      <p className="text-xs font-medium line-clamp-2 group-hover:text-purple-300 transition-colors" style={{ color: '#E2E8F0' }}>{t.title}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* ===== MOST SHARED QUOTES ===== */}
        {sharedQuotes.length > 0 && (
          <section className="py-10 border-t" style={{ borderColor: 'rgba(255,255,255,0.04)' }} data-testid="quotes-section">
            <div className="flex items-center gap-2.5 mb-6">
              <Quote className="w-5 h-5 text-purple-400" />
              <div>
                <h2 className="text-xl sm:text-2xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}>Most Shared This Week</h2>
                <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>Top quotes from recent debates</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sharedQuotes.map((q, i) => (
                <motion.div key={q.quote_id || `quote-${i}`} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
                  className="rounded-2xl border p-5 relative overflow-hidden" style={{ backgroundColor: 'rgba(15,15,18,0.8)', borderColor: 'rgba(255,255,255,0.06)' }}>
                  <div className="absolute top-3 right-3 opacity-[0.06]"><Quote className="w-12 h-12" /></div>
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-8 h-8 rounded-full border overflow-hidden" style={{ borderColor: '#6D28D9' }}>
                      <img src={EXPERT_IMAGES[q.expert_id] || HOST_IMAGE} alt={q.speaker} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-xs font-medium" style={{ color: '#F8FAFC' }}>{q.speaker}</p>
                      <p className="text-[10px]" style={{ color: '#64748B' }}>{q.title}</p>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed italic mb-3" style={{ color: '#D1D5DB' }}>"{q.text}"</p>
                  <p className="text-[10px]" style={{ color: '#6D28D9' }}>from: {q.topic}</p>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* ===== BOTTOM CTA ===== */}
        <section className="py-16 text-center border-t" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Sparkles className="w-6 h-6 text-purple-500 mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}>
              Any Topic. Real Debate. Real Voices.
            </h2>
            <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: '#64748B' }}>28 AI experts with opposing views are waiting to debate whatever you throw at them.</p>
            {isGuest ? (
              <Button onClick={() => navigate('/auth', { state: { register: true } })} className="bg-purple-600 hover:bg-purple-500 text-white px-8 h-12 rounded-xl font-medium" data-testid="bottom-cta-btn">
                <Radio className="w-4 h-4 mr-2" />Get Started Free
              </Button>
            ) : (
              <Button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="bg-purple-600 hover:bg-purple-500 text-white px-8 h-12 rounded-xl font-medium" data-testid="bottom-cta-btn">
                <Radio className="w-4 h-4 mr-2" />Start a Debate
              </Button>
            )}
          </motion.div>
        </section>

        {/* ===== FOOTER ===== */}
        <footer className="border-t py-8" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-purple-500" />
              <span className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#64748B' }}>Opposite Direction Podcast</span>
              <span className="text-xs" style={{ color: '#475569' }}>&copy; {new Date().getFullYear()}</span>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-[11px]" style={{ color: '#64748B' }}>
              <Link to="/terms" className="hover:text-purple-400 transition-colors" data-testid="footer-terms">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-purple-400 transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/cookies" className="hover:text-purple-400 transition-colors" data-testid="footer-cookies">Cookie Policy</Link>
              <Link to="/refund-policy" className="hover:text-purple-400 transition-colors" data-testid="footer-refund">Refund Policy</Link>
              <Link to="/community-guidelines" className="hover:text-purple-400 transition-colors" data-testid="footer-community">Community Guidelines</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HomePage;
